import { useCallback, useEffect, useRef, useState } from "react";
import { useUnmount } from "react-use";
import { LiveChatJoinResponse } from "@scrile/api-provider/dist/api/LivechatsProvider";
import { WebRTCConnection } from "@scrile/api-provider/dist/api/WebRTCProvider";
import { AudioAnalyzerOptions } from "@scrile/streaming-client/build/AudioAnalyzer";
import { isEqual, uniqBy } from "lodash-es";
import {
  addTracksToStream,
  cloneScreenTracks,
  filterMediaDevices,
  getScreenTracks,
  getUserMediaWithFallback,
  getVideoTracks,
  removeTracksFromStream,
} from "lib/mediaStreamHelpers";
import providers from "lib/providers";
import { MediaConstraints } from "types";
import useBlurController from "./useBlurController";
import useMediaConstraints from "./useMediaConstraints";
import useMediaDevicePermissions from "./useMediaDevicePermissions";
import useWebRTC from "./useWebRTC";
import { defaultMediaConstraints, SendDataEvent } from "../const";

interface ProduceProps {
  token: string;
  joinData: LiveChatJoinResponse | null;
  autoProduceStream: boolean;
  audioAnalyzer?: boolean;
}

function useProduceController({ token, joinData, autoProduceStream, audioAnalyzer }: ProduceProps) {
  const { blurStart, blurStop } = useBlurController();

  const [stream, setStream] = useState<MediaStream>(new MediaStream([]));
  const [screenStream, setScreenStream] = useState<MediaStream>(new MediaStream([]));
  const [streamToProduce, setStreamToProduce] = useState<MediaStream>(new MediaStream([]));
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [producerSpeaking, setProducerSpeaking] = useState(false);
  const [audioInputs, setAudioInputs] = useState<MediaDeviceInfo[]>([]);
  const [videoInputs, setVideoInputs] = useState<MediaDeviceInfo[]>([]);

  const connection = useRef<WebRTCConnection>();
  const onChangeConstraintsRef = useRef<(constraints: MediaConstraints) => Promise<void>>();

  const { mediaConstraints, setMediaConstraints } = useMediaConstraints({
    ...defaultMediaConstraints,
    videoEnabled: autoProduceStream,
    audioEnabled: autoProduceStream,
  });

  const audioAnalyzerOptions: AudioAnalyzerOptions | undefined = audioAnalyzer
    ? {
        onAudioActivity: ({ isSpeaking }: { isSpeaking: boolean }) => {
          setProducerSpeaking(isSpeaking);
        },
        threshold: 1, // the lower the value, the more sensitive. Empirically obtained
        analysisInterval: 500, // in ms
      }
    : undefined;
  const WebRTC = useWebRTC({ audioAnalyzerOptions });

  const setStreams = (stream: MediaStream) => {
    const screenTracks = getScreenTracks(stream);
    screenTracks.forEach((t) => {
      t.onended = () => onChangeConstraintsRef.current?.({ shareEnabled: false });
    });
    setStreamToProduce(new MediaStream(stream));
    setStream(new MediaStream(getVideoTracks(stream)));
    setScreenStream(new MediaStream(screenTracks));
  };

  const onScreenShare = async (constraints: MediaConstraints) => {
    let data: MediaStreamConstraints | MediaStream;
    if (constraints.shareEnabled) {
      // @ts-ignore
      const screen = (await navigator.mediaDevices.getDisplayMedia()) as MediaStream;
      const screenTracks = screen.getVideoTracks();
      // @ts-ignore
      screenTracks.forEach((t) => (t.kindType = "screen"));
      data = new MediaStream([...screen.getTracks(), ...streamToProduce.getVideoTracks()]);
      addTracksToStream(data, streamToProduce.getAudioTracks());
    } else {
      removeTracksFromStream(streamToProduce, getScreenTracks(streamToProduce));
      data = new MediaStream(streamToProduce.getVideoTracks());
      addTracksToStream(data, streamToProduce.getAudioTracks());
    }
    return data;
  };

  const onVideoChange = async (constraints: MediaConstraints) => {
    let data: MediaStream;

    removeTracksFromStream(streamToProduce, getVideoTracks(streamToProduce));
    if (!constraints.videoEnabled) {
      data = new MediaStream(streamToProduce.getVideoTracks());
      addTracksToStream(data, streamToProduce.getAudioTracks());
    } else {
      const newStream = new MediaStream(
        await getUserMediaWithFallback(
          { video: constraints.video },
          {
            onOverconstrained: () => {
              constraints.video = true;
            },
            onNotAllowed: () => {
              constraints.videoEnabled = false;
            },
          }
        )
      );
      addTracksToStream(newStream, streamToProduce.getAudioTracks());
      cloneScreenTracks(getScreenTracks(streamToProduce)).forEach((t) => newStream.addTrack(t));
      data = newStream;
    }
    return data;
  };

  const onAudioChange = async (constraints: MediaConstraints) => {
    const audioChanged = !isEqual(mediaConstraints.audio, constraints.audio);
    const audioTracks = streamToProduce.getAudioTracks();

    if (audioChanged) {
      removeTracksFromStream(streamToProduce, audioTracks);
    }
    if (constraints.audioEnabled) {
      if (audioChanged || !audioTracks.length) {
        const newStream = await getUserMediaWithFallback(
          { audio: constraints.audio },
          {
            onOverconstrained: () => {
              constraints.audio = true;
            },
            onNotAllowed: () => {
              constraints.audioEnabled = false;
            },
          }
        );
        addTracksToStream(streamToProduce, newStream.getAudioTracks(), true);
      } else {
        audioTracks.forEach((t) => (t.enabled = true));
      }
    } else {
      audioTracks.forEach((t) => (t.enabled = false));
    }
    return streamToProduce;
  };

  const onBlurChange = useCallback(
    async (constraints: MediaConstraints, outStream = streamToProduce) => {
      let data: MediaStream;

      if (constraints.blurEnabled) {
        const videoTracks = getVideoTracks(outStream);
        if (!videoTracks.length) {
          constraints.blurEnabled = false;
          return outStream;
        }
        data = await blurStart(videoTracks[0]);
      } else {
        data = await blurStop();
      }

      addTracksToStream(data, outStream.getAudioTracks());
      cloneScreenTracks(getScreenTracks(streamToProduce)).forEach((t) => data.addTrack(t));
      return data;
    },
    [blurStart, blurStop, streamToProduce]
  );

  const setDevicesList = useCallback(async () => {
    try {
      const { audioInputList, videoInputList } = await WebRTC.getDeviceList();
      setAudioInputs(uniqBy(filterMediaDevices(audioInputList).reverse(), "groupId").reverse());
      setVideoInputs(filterMediaDevices(videoInputList));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  }, [WebRTC]);

  const permissions = useMediaDevicePermissions(setDevicesList);

  const closeStream = async () => {
    await onProducerFinishedStream();
    WebRTC.closeAllStreams();
    streamToProduce.getTracks().forEach((t) => t.stop());
    stream.getTracks().forEach((t) => t.stop());
    screenStream?.getTracks().forEach((t) => t.stop());
  };

  const onProduceStream = useCallback(async () => {
    try {
      setLoading(true);
      if (!token) return false;
      connection.current = await providers.LivechatsProvider.broadcast({ token });

      let localStream = new MediaStream([]);
      if (mediaConstraints.videoEnabled || mediaConstraints.audioEnabled) {
        localStream = await getUserMediaWithFallback(
          {
            video: mediaConstraints.videoEnabled ? mediaConstraints.video : false,
            audio: mediaConstraints.audioEnabled ? mediaConstraints.audio : false,
          },
          {
            onOverconstrained: () => {
              setMediaConstraints({
                ...mediaConstraints,
                audio: mediaConstraints.audioEnabled ? true : mediaConstraints.audio,
                video: mediaConstraints.videoEnabled ? true : mediaConstraints.video,
              });
            },
            onNotAllowed: () => {
              setMediaConstraints({
                ...mediaConstraints,
                audioEnabled: false,
                videoEnabled: false,
              });
            },
          }
        );

        if (mediaConstraints.blurEnabled) {
          localStream = await onBlurChange({ ...mediaConstraints }, localStream);
        }
      }

      localStream = await WebRTC.produceStream(connection.current, localStream);
      setStreams(localStream);
      return true;
    } finally {
      setLoading(false);
    }
  }, [WebRTC, token, mediaConstraints, onBlurChange, setMediaConstraints]);

  const onChangeConstraints = async (constraints: MediaConstraints) => {
    try {
      setProcessing(true);
      let data: MediaStream | null = null;
      const localConstraints: MediaConstraints = {
        ...mediaConstraints,
        ...constraints,
      };
      if (!isEqual(mediaConstraints.blurEnabled, localConstraints.blurEnabled)) {
        data = await onBlurChange(localConstraints);
      } else if (!isEqual(mediaConstraints.shareEnabled, localConstraints.shareEnabled)) {
        data = await onScreenShare(localConstraints);
      } else if (
        !isEqual(mediaConstraints.videoEnabled, localConstraints.videoEnabled) ||
        !isEqual(mediaConstraints.video, localConstraints.video)
      ) {
        if (localConstraints.blurEnabled) {
          await blurStop();
        }
        data = await onVideoChange(localConstraints);
        if (localConstraints.blurEnabled && localConstraints.videoEnabled) {
          data = await onBlurChange(localConstraints, data);
        }
      } else if (
        !isEqual(mediaConstraints.audio, localConstraints.audio) ||
        !isEqual(mediaConstraints.audioEnabled, localConstraints.audioEnabled)
      ) {
        data = await onAudioChange(localConstraints);
      }
      if (!connection.current || !data) {
        setMediaConstraints(localConstraints);
        return;
      }
      const newStream = await WebRTC.produceStream(connection.current, data);
      setStreams(newStream);
      setMediaConstraints(localConstraints);
    } finally {
      setProcessing(false);
    }
  };

  onChangeConstraintsRef.current = onChangeConstraints;

  const shouldCallProduceStream = useRef(true);
  useEffect(() => {
    if (joinData && shouldCallProduceStream.current && autoProduceStream) {
      shouldCallProduceStream.current = false;
      onProduceStream();
    }
  }, [joinData, onProduceStream, autoProduceStream]);

  useUnmount(closeStream);

  const onProducerFinishedStream = async () => {
    await WebRTC.webRTCData?.producer.client?.sendData({ event: SendDataEvent.PRODUCER_FINISHED_STREAM });
  };

  return {
    processing,
    loading,
    screenStream,
    stream,
    streamToProduce,
    connection: connection.current,
    mediaConstraints,
    permissions,
    audioInputs,
    videoInputs,
    producerSpeaking,
    onProduceStream,
    onProducerFinishedStream,
    onChangeConstraints,
    closeStream,
  };
}

export default useProduceController;
